/**
 * Browser-only readiness contract for Google tag destinations.
 *
 * `window.gtag` is created by the inline all-denied Consent Mode shim, so its
 * presence only means commands can be queued. It does not mean a destination
 * has been granted consent and configured. GoogleAnalytics marks a destination
 * ready only after those commands have been queued in that order; event
 * producers use this module so they cannot jump ahead of consent/config.
 */

export type GoogleTagDestination = "analytics" | "advertising";

export const GOOGLE_TAG_DESTINATION_READY_EVENT =
  "tsb_google_tag_destination_ready";

declare global {
  interface Window {
    __tsbGoogleTagReadiness?: Partial<Record<GoogleTagDestination, boolean>>;
  }
}

export function isGoogleTagDestinationReady(
  destination: GoogleTagDestination,
): boolean {
  return (
    typeof window !== "undefined" &&
    window.__tsbGoogleTagReadiness?.[destination] === true
  );
}

/** Called by the Google tag loader after its ordered consent/config enqueue. */
export function setGoogleTagDestinationReady(
  destination: GoogleTagDestination,
  ready: boolean,
): void {
  if (typeof window === "undefined") return;

  const current = window.__tsbGoogleTagReadiness?.[destination] === true;
  if (current === ready) return;

  window.__tsbGoogleTagReadiness = {
    ...window.__tsbGoogleTagReadiness,
    [destination]: ready,
  };

  if (ready) {
    window.dispatchEvent(
      new CustomEvent(GOOGLE_TAG_DESTINATION_READY_EVENT, {
        detail: { destination },
      }),
    );
  }
}

export function subscribeToGoogleTagDestinationReady(
  destination: GoogleTagDestination,
  callback: () => void,
): () => void {
  if (typeof window === "undefined") return () => {};

  const listener = (event: Event) => {
    if (!(event instanceof CustomEvent)) return;
    if (event.detail?.destination !== destination) return;
    callback();
  };
  window.addEventListener(GOOGLE_TAG_DESTINATION_READY_EVENT, listener);
  return () =>
    window.removeEventListener(GOOGLE_TAG_DESTINATION_READY_EVENT, listener);
}

/**
 * Queue an event only after the destination's consent/config commands.
 * Returns true only when the event reached the gtag queue, so callers can set
 * fire-once guards without consuming their retry opportunity prematurely.
 */
export function sendGoogleTagEventIfReady(
  destination: GoogleTagDestination,
  eventName: string,
  params: Record<string, unknown>,
): boolean {
  if (
    !isGoogleTagDestinationReady(destination) ||
    typeof window === "undefined" ||
    typeof window.gtag !== "function"
  ) {
    return false;
  }

  window.gtag("event", eventName, params);
  return true;
}

/** Wait briefly for the loader effect when consent is already accepted. */
export function waitForGoogleTagDestinationReady(
  destination: GoogleTagDestination,
  timeoutMs = 1_000,
): Promise<boolean> {
  if (isGoogleTagDestinationReady(destination)) return Promise.resolve(true);
  if (typeof window === "undefined") return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      // The timeout path can fire after the environment is gone (jsdom
      // teardown between test files) — nothing is left to clean up then.
      if (typeof window !== "undefined") {
        unsubscribe();
        window.clearTimeout(timeout);
      }
      resolve(ready);
    };
    const unsubscribe = subscribeToGoogleTagDestinationReady(destination, () =>
      finish(isGoogleTagDestinationReady(destination)),
    );
    const timeout = window.setTimeout(() => finish(false), timeoutMs);

    // Close the check-to-subscribe race if readiness changed synchronously.
    if (isGoogleTagDestinationReady(destination)) finish(true);
  });
}
